// The record pages' Files panel (window.FilesPanel), moved out of
// deal_detail.jsx. The attachment helpers it shares with the note chips and
// the composer (useFileDrop, handOffFilesToComposer, ATTACHMENT_EXT_HUE,
// _fmtSize) stay owned by deal_detail.jsx and are read here through window.*.

// A phone sheet's loading / empty value (no explainer copy); `terse` (the vault's All
// attachments) uses the same dimmed value at desktop size.
const FP_PHONE_DIM = { padding: '14px 16px', fontSize: 15, color: 'var(--text-4)' };
const FP_DESK_DIM = { padding: '14px', fontSize: 12, color: 'var(--text-4)' };

// ── Files panel: the vault's All attachments, the Files workspace ────────
// Lists every attachment surfaced from notes linked to this company / deal /
// contact. Tabs: All · Available · Missing · By type. Click → opens in a new
// tab (when available). "Missing" entries are kept visible so the historical
// reference stays honest.
const FilesPanel = ({ scope = {}, title = 'Files', terse = false }) => {
  const { useFileDrop, handOffFilesToComposer } = window;
  // Phone (step 15): only ever in a sheet (the vault's All attachments, the Files
  // workspace), whose header names it, so no title row; 44px tabs, 56px rows, and
  // a dimmed "—" / "0" while loading / empty.
  const phone = !!(window.AtlasDevice && window.AtlasDevice.phone);
  // A dimmed '—' / '0' instead of the loading / empty copy: every phone, and the vault pane.
  const dim = (v) => ((phone || terse) ? <div style={phone ? FP_PHONE_DIM : FP_DESK_DIM}>{v}</div> : null);
  // scope = { company_id, engagement_id, contact_id } — server filters by these
  const [items, setItems] = React.useState(null);
  const [filter, setFilter] = React.useState('available'); // available | all | missing
  // Attach/detach happens in the composer and the note modal — outside this
  // panel — so AtlasApi fires this event after every successful change.
  const [attachTick, setAttachTick] = React.useState(0);
  React.useEffect(() => {
    const bump = () => setAttachTick((t) => t + 1);
    window.addEventListener('atlas-attachments-changed', bump);
    return () => window.removeEventListener('atlas-attachments-changed', bump);
  }, []);

  React.useEffect(() => {
    const params = { limit: 500 };
    if (scope.company_id)    params.company_id    = scope.company_id;
    if (scope.engagement_id) params.engagement_id = scope.engagement_id;
    if (scope.contact_id)    params.contact_id    = scope.contact_id;
    setItems(null);
    window.AtlasApi.listAttachments(params)
      .then((r) => setItems((r && r.items) || []))
      .catch(() => setItems([]));
  }, [scope.company_id, scope.engagement_id, scope.contact_id, attachTick]);

  const rows = (items || []).filter((a) => {
    if (filter === 'available') return a.storage_status === 'available';
    if (filter === 'missing')   return a.storage_status === 'referenced_only';
    return true;
  });

  const counts = React.useMemo(() => {
    if (!items) return { all: 0, available: 0, missing: 0 };
    return {
      all: items.length,
      available: items.filter((a) => a.storage_status === 'available').length,
      missing:   items.filter((a) => a.storage_status === 'referenced_only').length,
    };
  }, [items]);

  // This list is read-only — there's no file-without-a-note — so a drop here
  // routes into the page's note composer instead of inventing an upload of
  // its own. Same useFileDrop() wiring as the other attach surfaces.
  const [dragOver, dropHandlers] = useFileDrop((files) => handOffFilesToComposer(files, scope));

  return (
    <div
      {...dropHandlers}
      style={{
        // Outline (not border) so arming the drop zone can't shift the rail's
        // layout; inset so it stays inside the panel's own edges.
        outline: dragOver ? '2px dashed var(--accent)' : 'none', outlineOffset: -2,
        background: dragOver ? 'var(--accent-soft)' : 'transparent',
      }}
    >
      {!phone && (
        <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon.Doc size={13} color="var(--accent)" />
          <span style={{ fontSize: 13, fontWeight: 600 }}>{title}</span>
          <span style={{
            marginLeft: 'auto', fontSize: 10.5,
            color: dragOver ? 'var(--accent-text)' : 'var(--text-4)', fontWeight: dragOver ? 600 : 400,
          }}>{dragOver ? 'Drop to attach to a note' : (items ? items.length : '')}</span>
        </div>
      )}

      {items === null && (dim('—') || (
        <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>
      ))}

      {items && items.length === 0 && (dim('0') || (
        <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>
          No files attached to notes here yet.
        </div>
      ))}

      {items && items.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: phone ? 4 : 0, padding: phone ? '6px 12px' : '6px 10px', borderBottom: '1px solid var(--border)' }}>
            {[
              ['available', 'Available', counts.available],
              ['missing',   'Missing',   counts.missing],
              ['all',       'All',       counts.all],
            ].map(([k, label, n]) => {
              const active = filter === k;
              return (
                <button key={k} onClick={() => setFilter(k)} style={{
                  padding: '3px 8px', border: 'none', borderRadius: 5,
                  background: active ? 'var(--surface-2)' : 'transparent',
                  color: active ? 'var(--text)' : 'var(--text-3)',
                  cursor: 'pointer', fontSize: 11, fontWeight: active ? 600 : 500,
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  ...(phone ? { flex: 1, justifyContent: 'center', minHeight: 44, padding: '0 10px', borderRadius: 8, fontSize: 14, gap: 6 } : null),
                }}>
                  {label}<span style={{ fontSize: phone ? 13 : 10, color: 'var(--text-4)' }}>{n}</span>
                </button>
              );
            })}
          </div>
          <div>
            {rows.map((a, i) => (
              <FileRow key={a.id} att={a} isFirst={i === 0} phone={phone} />
            ))}
            {rows.length === 0 && (dim('0') || (
              <div style={{ padding: '14px', fontSize: 12, color: 'var(--text-3)' }}>
                Nothing in this filter.
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const FileRow = ({ att, isFirst, phone = false }) => {
  const { ATTACHMENT_EXT_HUE, _fmtSize } = window;
  const available = att.storage_status === 'available' && att.storage_uri;
  const inline = window.AtlasApi.attachmentOpensInline(att.original_filename);
  const hue = ATTACHMENT_EXT_HUE[(att.file_extension || '').toLowerCase()] || 'slate';
  const h = HUE[hue] || HUE.slate;
  const sizeStr = _fmtSize(att.size_bytes);
  const dateStr = att.dc_created_at
    ? new Date(att.dc_created_at).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' })
    : null;
  const Tag = available ? 'a' : 'div';
  const tagProps = available
    ? { href: '#', onClick: (e) => { e.preventDefault(); window.AtlasApi.openAttachment(att.id, att.original_filename); } }
    : {};
  return (
    <Tag
      {...tagProps}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
        borderTop: isFirst ? 'none' : '1px solid var(--border)',
        cursor: available ? 'pointer' : 'default', textDecoration: 'none', color: 'inherit',
        background: 'transparent',
        ...(phone ? { minHeight: 56, padding: '8px 16px', gap: 12, boxSizing: 'border-box', WebkitTapHighlightColor: 'transparent' } : null),
      }}
      onMouseEnter={(e) => { if (available) e.currentTarget.style.background = 'var(--surface-2)'; }}
      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      title={!available ? 'Referenced in DealCloud but the file is no longer in the backup.' : inline ? 'Open in new tab' : 'Download'}
    >
      <span style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: phone ? 32 : 26, height: phone ? 32 : 26, borderRadius: 6, flexShrink: 0,
        background: available ? h.bg : 'var(--surface-2)',
        color:      available ? h.fg : 'var(--text-4)',
        fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
      }}>
        {(att.file_extension || '·').slice(0, 4)}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: phone ? 15 : 12, fontWeight: 500,
          color: available ? 'var(--text)' : 'var(--text-3)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{att.original_filename}</div>
        <div style={{ display: 'flex', gap: 6, fontSize: phone ? 12.5 : 10.5, color: 'var(--text-4)', marginTop: phone ? 2 : 1 }}>
          {sizeStr && <span>{sizeStr}</span>}
          {dateStr && <><span>·</span><span>{dateStr}</span></>}
          {!available && <><span>·</span><Pill hue="slate" sz="sm">missing</Pill></>}
        </div>
      </div>
      {available && (inline ? <Icon.External size={11} color="var(--text-4)" /> : <Icon.Download size={11} color="var(--text-4)" />)}
    </Tag>
  );
};

window.FilesPanel = FilesPanel;
